// Shared GitHub OIDC auth-method constants. Single source of truth for both the
// scaffolder picker (@runway/devkit) and the secret-vault plugin so the UI selector,
// default, and repo-name validation cannot drift between the two entry points.

// GitHub organizations selectable when configuring a GitHub OIDC auth method.
// The SVS backend enforces its own allow-list (GITHUB_ALLOWED_ORGS) and rejects
// anything off-list with a 400 — this list only drives the UI selector.
export const GITHUB_ALLOWED_ORGS = ['AAInternal', 'Internal-AA', 'TestingOrg-AA'] as const;

// Matches SVS default: an omitted github_org resolves to AAInternal.
export const DEFAULT_GITHUB_ORG: (typeof GITHUB_ALLOWED_ORGS)[number] = 'AAInternal';

// SVS repo-name rules: bare name (no `org/`), <=150 chars, [A-Za-z0-9-_] only.
export const GITHUB_REPO_NAME_REGEX = /^[A-Za-z0-9_-]{1,150}$/;

export type GithubRepository = {
  owner: string;
  repo: string;
};

export const toGithubRepositoryRequest = (githubOrg: string, repositories: GithubRepository[]) => {
  const owner = githubOrg.trim();
  if (!owner) {
    throw new Error('GitHub organization is required');
  }

  const repoNames = repositories.map(repository => {
    const repositoryOwner = repository.owner.trim();
    if (!repositoryOwner) {
      throw new Error('GitHub repository owner is required');
    }
    if (repositoryOwner.toLowerCase() !== owner.toLowerCase()) {
      throw new Error(`All GitHub repositories must belong to ${owner}`);
    }

    const repo = repository.repo.trim();
    if (!repo) {
      throw new Error('GitHub repository name is required');
    }
    if (!GITHUB_REPO_NAME_REGEX.test(repo)) {
      throw new Error(`Invalid GitHub repository name: ${repo}`);
    }
    return repo;
  });

  return { github_org: owner, github_repo: repoNames };
};
